/**
 * Translation between the checkout's consent decision and Google's consent
 * mode v2 signals, plus the clean-up required when consent is withdrawn.
 *
 * Guest Checkout is the only writer of these signals: Termly's own consent mode
 * output is suppressed (see `TermlyCookieConsentProvider`) because it follows
 * region defaults from the Termly dashboard rather than the decision recorded
 * for this visitor.
 */
import { deleteCookie, listCookieNames } from "../utils/cookies";
import { isSharedConsentHost, SHARED_COOKIE_DOMAIN } from "../services/cookieConsent/TermlyCookieConsentProvider";
import type { ConsentCategories } from "../services/cookieConsent/types";

/** Google consent mode signal states. */
export type ConsentSignalState = "granted" | "denied";

/** The consent mode v2 signals the checkout sets. */
export type ConsentSignal =
  | "ad_storage"
  | "ad_user_data"
  | "ad_personalization"
  | "analytics_storage"
  | "functionality_storage"
  | "personalization_storage"
  | "security_storage";

export type GoogleConsentState = Record<ConsentSignal, ConsentSignalState>;

/**
 * Everything refused except `security_storage`, which covers storage needed for
 * security and is not consent bearing. Sent as the `default` state before any
 * Google library loads, so a tag that somehow loads early cannot read or write
 * storage before the visitor has decided.
 */
export const DENIED_CONSENT_STATE: GoogleConsentState = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
  functionality_storage: "denied",
  personalization_storage: "denied",
  security_storage: "granted",
};

/**
 * Maps consent categories onto Google's signals, using the same mapping Termly
 * applies natively: advertising drives all three advertising signals, analytics
 * drives analytics storage, and performance drives functionality and
 * personalisation storage.
 */
export function toGoogleConsentState(categories: ConsentCategories): GoogleConsentState {
  const signal = (category: keyof ConsentCategories): ConsentSignalState =>
    categories[category] ? "granted" : "denied";

  return {
    ad_storage: signal("advertising"),
    ad_user_data: signal("advertising"),
    ad_personalization: signal("advertising"),
    analytics_storage: signal("analytics"),
    functionality_storage: signal("performance"),
    personalization_storage: signal("performance"),
    security_storage: "granted",
  };
}

/**
 * Whether the checkout owns the visitor's consent decision on this host.
 *
 * Embedded on a venue's own domain the checkout is a third-party component: the
 * embedder's consent platform governs what may run on their page, so no Google
 * library is injected there regardless of the checkout's own banner.
 */
export function ownsConsentDecision(hostname: string | undefined = globalThis.location?.hostname): boolean {
  return isSharedConsentHost(hostname);
}

/**
 * Cookie names, exact or by prefix, that the Google libraries the checkout
 * injects may write.
 */
const GOOGLE_COOKIE_PREFIXES = ["_ga", "_gid", "_gat", "_gcl_", "_gac_"];

/**
 * Deletes the Google first-party cookies after consent is withdrawn.
 *
 * Setting the signals to denied stops further storage, but the ICO requires
 * storage already placed to be removed when consent is withdrawn, so the
 * cookies are cleared from both this host and the shared apex domain (where the
 * checkout is hosted on it). Cookies belonging to an embedding page are out of
 * scope: they are not readable from here, and are the embedder's to remove.
 */
export function clearGoogleCookies(): void {
  const hostname = globalThis.location?.hostname;

  for (const name of listCookieNames()) {
    if (!GOOGLE_COOKIE_PREFIXES.some((prefix) => name.startsWith(prefix))) continue;

    deleteCookie(name);
    if (hostname) deleteCookie(name, { domain: hostname });
    if (isSharedConsentHost(hostname)) deleteCookie(name, { domain: SHARED_COOKIE_DOMAIN });
  }
}
